import { useNavigate, useSearch } from "@tanstack/react-router";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth";

import type { PostHogSearch, PostHogTab } from "..";
import { GoalsTab } from "../components/GoalsTab";
import { HowToTab } from "../components/HowToTab";
import { OverviewTab } from "../components/OverviewTab";
import { SettingsTab } from "../components/SettingsTab";

const WINDOWS = [
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

export function PostHogPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/system/posthog" }) as PostHogSearch;
  // Settings holds the API keys, so it's superuser-only. A direct URL hit
  // without the permission falls back to overview rather than 403-ing —
  // the same idiom as cash_flow's admin tab.
  const canManageKeys = useCan("*");
  const requested: PostHogTab = search.tab ?? "overview";
  const tab: PostHogTab =
    requested === "settings" && !canManageKeys ? "overview" : requested;
  const days = search.days ?? 7;

  const setSearch = (next: Partial<PostHogSearch>) =>
    navigate({
      to: "/system/posthog",
      search: (prev: PostHogSearch) => ({ ...prev, ...next }),
    });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">PostHog</h1>
          <p className="text-sm text-muted-foreground">
            Which features people actually use, who uses them, and how that compares
            to what each feature was built for.
          </p>
        </div>
        {tab === "overview" ? (
          <Select
            value={String(days)}
            onValueChange={(v) => setSearch({ days: Number(v) })}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Window" />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.value} value={String(w.value)}>
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>

      <Tabs value={tab} onValueChange={(v) => setSearch({ tab: v as PostHogTab })}>
        {/* underline is the house style for top-level section nav inside a
            page — see the variant note in components/ui/tabs.tsx. */}
        <TabsList variant="underline">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          {canManageKeys ? <TabsTrigger value="settings">Settings</TabsTrigger> : null}
          <TabsTrigger value="how-to">How-To</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab days={days} />
        </TabsContent>
        <TabsContent value="goals" className="mt-4">
          <GoalsTab days={days} />
        </TabsContent>
        {canManageKeys ? (
          <TabsContent value="settings" className="mt-4">
            <SettingsTab />
          </TabsContent>
        ) : null}
        <TabsContent value="how-to" className="mt-4">
          <HowToTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
